import React, { useState } from 'react';
import { Modal, Button, Form, Select, Col, Row } from 'antd';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { ChevronDown, ChevronUp, CloseIcon, ArrowRight } from '../../assets/icons';
import FloatingLabel from '../FloatingLabel/FloatingLabel';
import { UploadField } from '../SharedComponents/FormComponents/UploadField';
import { uploadVerificationDocApi, getUserProfileApi } from '../../services/authApi/userApi';
import { uploadIdentificationDocuments } from '../../utils/identificationDocuments';
import { toastNotify } from '../../utils/toastNotify';
import { addUserData, addCounter } from '../../store/reducer/accountSlice';
import { useIdentificationUpload } from '../../Hooks/useIdentificationUpload';
import './SecondaryMarketIdentityUpload.scss';

const SecondaryMarketIdentityUpload = ({ visible, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [arrowUp, setArrowUp] = useState(false);
  const [form] = Form.useForm();

  const {
    identificationType,
    fileListFront,
    fileListBack,
    fileListPassport,
    fileValidate,
    fileSizeValidate,
    fileFrontValidate,
    fileFrontSizeValidate,
    fileBackValidate,
    fileBackSizeValidate,
    onChangeFront,
    onChangeBack,
    onChangePassport,
    beforeUpload,
    handleRemoveUploaded,
    handleIdentificationChange,
    resetIdentificationState,
    buildUploadsData,
    isUploadInvalid,
    isUploadIncomplete,
  } = useIdentificationUpload();

  const onSubmit = async () => {
    setLoading(true);

    try {
      const consolidatedDocs = await uploadIdentificationDocuments(buildUploadsData());

      if (Object.keys(consolidatedDocs).length > 0) {
        const uploadResponse = await uploadVerificationDocApi(consolidatedDocs);

        if (uploadResponse?.isError) {
          toastNotify('error', uploadResponse?.message || 'Failed to save verification documents');
          // Refresh profile so UI reflects latest server state even on failure
          try {
            const profileData = await getUserProfileApi();
            if (!profileData?.isError) {
              dispatch(addUserData({ userData: { ...profileData?.data?.user } }));
              dispatch(addCounter(profileData?.data?.counter));
            }
          } catch (_) {}
          return;
        }

        // Show success message from backend or default message
        const successMessage = uploadResponse?.message || 'Identity documents uploaded successfully';
        toastNotify('success', successMessage);

        // Refresh profile so WarningBar / toast error conditions reflect latest server state
        try {
          const profileData = await getUserProfileApi();
          if (!profileData?.isError) {
            dispatch(addUserData({ userData: { ...profileData?.data?.user } }));
            dispatch(addCounter(profileData?.data?.counter));
          }
        } catch (_) {}

        // Reset internal form state without triggering onClose (which would reset parent modal state)
        form.resetFields();
        resetIdentificationState();

        // Call parent success handler — this advances state to 'AccessModal_Agreement'
        onSuccess(consolidatedDocs);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in upload process:', error);
      const errorMessage = error?.response?.data?.message || 'Failed to upload verification documents';
      toastNotify('error', errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    resetIdentificationState();
    onClose();
  };

  return (
    <Modal
      className='secondary-market-identity-upload-modal'
      open={visible}
      onCancel={handleClose}
      closeIcon={<CloseIcon />}
      footer={false}
      width={680}
      centered
    >
       <Form
          form={form}
          className='auth-form uploadid'
          name='uploadIDForm'
          onFinish={onSubmit}
        >
          <h4>ID Upload Required</h4>
          <p>Please use one of the following valid forms of photo identification.</p>
          <ul style={{ marginLeft: '20px', marginBottom: '24px' }}>
            <li>U.S. State-Issued Driver's License</li>
            <li>U.S. State-Issued Identification Card (Non-Driver ID)</li>
            <li>U.S. Passport (Book)</li>
            <li>U.S. Passport (Card)</li>
            <li>U.S. Visa</li>
            <li>Federal Employment Authorization Card</li>
          </ul>
          <p>Please upload a valid, US government-issued ID. The image must be clear and easy to read. Accepted file types: JPEG, JPG, or PNG.</p>
          <FloatingLabel label='Type of identification' inputId='identification' inputValue={identificationType}>
            <>
              <div id='identification-anchor' />
              <Form.Item name='identification' rules={[{ required: true, message: 'Please select identification type' }]}>
                <Select
                  disabled={loading}
                  id='identification'
                  onChange={handleIdentificationChange}
                  value={identificationType}
                  getPopupContainer={(trigger) => trigger.closest('.ant-modal-body') || document.body}
                  onDropdownVisibleChange={() => setArrowUp(!arrowUp)}
                  suffixIcon={arrowUp ? <ChevronUp color='#1A1519' /> : <ChevronDown color='#1A1519' />}
                >
                  <Select.Option value='passport'>Passport</Select.Option>
                  <Select.Option value='license'>ID</Select.Option>
                </Select>
              </Form.Item>
            </>
          </FloatingLabel>
          {identificationType === 'license' && (
            <Row gutter={20}>
              <Col span={24} md={12}>
                <label htmlFor='licenseFront' className='sr-only'>
                  Upload front of ID
                </label>
                <Form.Item className='license-front'>
                  <UploadField
                    onChangeHandle={onChangeFront}
                    fileList={fileListFront}
                    id='licenseFront'
                    beforeUpload={(value) => beforeUpload(value, 'front')}
                    handleRemoveUploaded={() => handleRemoveUploaded(fileListFront[0].uid, 'front')}
                    label='Upload front of ID'
                    fileValidate={fileFrontValidate}
                    fileSizeValidate={fileFrontSizeValidate}
                    loading={loading}
                  />
                </Form.Item>
              </Col>
              <Col span={24} md={12}>
                <label htmlFor='licenseBack' className='sr-only'>
                  Upload back of ID
                </label>
                <Form.Item className='license-back'>
                  <UploadField
                    onChangeHandle={onChangeBack}
                    fileList={fileListBack}
                    id='licenseBack'
                    beforeUpload={(value) => beforeUpload(value, 'back')}
                    handleRemoveUploaded={() => handleRemoveUploaded(fileListBack[0].uid, 'back')}
                    label='Upload back of ID'
                    fileValidate={fileBackValidate}
                    fileSizeValidate={fileBackSizeValidate}
                    loading={loading}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          {identificationType === 'passport' && (
            <Row>
              <Col span={24} md={12}>
                <label htmlFor='passport' className='sr-only'>
                  Upload ID
                </label>
                <Form.Item className='passport'>
                  <UploadField
                    onChangeHandle={onChangePassport}
                    fileList={fileListPassport}
                    id='passport'
                    beforeUpload={(value) => beforeUpload(value, 'passport')}
                    handleRemoveUploaded={() => !loading && handleRemoveUploaded(fileListPassport[0].uid, 'passport')}
                    label='Upload ID'
                    fileValidate={fileValidate}
                    fileSizeValidate={fileSizeValidate}
                    loading={loading}
                  />
                </Form.Item>
              </Col>
            </Row>
          )}
          <Form.Item className='modal-submit-button'>
            <Button type='primary' htmlType='submit' className='ant-btn-primary--alt' loading={loading} disabled={isUploadIncomplete || isUploadInvalid}>
              Continue <ArrowRight marginLeft='10px' verticalAlign='middle' top='-2px' />
            </Button>
          </Form.Item>
        </Form>
    </Modal>
  );
};

SecondaryMarketIdentityUpload.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default SecondaryMarketIdentityUpload;
